import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { resumeFromRefreshToken } from '../auth/firebaseAuth';
import { PhraseForm } from '../vault/PhraseForm';
import { Loading } from '../shell/Loading';
import { guardLabel, rememberedGuard, unlockBrowser } from '../vault/trustFlow';
import type { GuardKind } from '../vault/trustedBrowser';
import { clearPersistedAccount, getAuth, persistedRefreshToken, setAuth } from './authSession';
import { adoptFromPeer } from './tabHandshake';
import { setSession } from './vaultSession';

/**
 * The way back in, over whatever page the reload happened on.
 *
 * A remembered browser holds everything it needs — a sealed key and, since the
 * account persists alongside it, a way to ask for the share. What it cannot do
 * is skip the guard: that is the whole point of having chosen one.
 *
 * This is deliberately NOT a route. Coming back after a reload is not
 * connecting, and sending someone to /connect to do it moved them off the page
 * they were reading and put a pairing screen in front of a browser that was
 * already paired. So the gate renders in place, and /connect goes back to
 * meaning the first time.
 *
 * Nothing navigates on success either. [setSession] is a store the shell is
 * already subscribed to, so opening the vault makes this component stop being
 * rendered — the page underneath simply appears.
 */

type Step =
  | { name: 'opening' }
  /** The guard did not answer — declined, or out of reach. The blob is still
   *  good, so this is one button rather than a fall back to the phone. */
  | { name: 'locked' }
  | { name: 'phrase'; wrong: boolean }
  /** Nothing stored can be opened any more, so the only way on is the phone. */
  | { name: 'gone'; why: string };

export function UnlockGate({ onAsking }: { onAsking?: (asking: boolean) => void }) {
  const [step, setStep] = useState<Step>({ name: 'opening' });
  const [guard, setGuard] = useState<GuardKind | null>(null);
  const [account, setAccount] = useState<string | undefined>(getAuth()?.email);

  const asking = step.name !== 'opening';
  useEffect(() => {
    onAsking?.(asking);
    return () => onAsking?.(false);
  }, [asking, onAsking]);

  useEffect(() => {
    void rememberedGuard().then(setGuard);
  }, []);

  const open = useCallback(async (phrase?: string) => {
    setStep({ name: 'opening' });

    // A sibling tab that is already open is the same browser, the same origin
    // and the same person, so it can hand the key over directly. Asked only
    // when nothing has been typed: once someone is answering a passphrase,
    // that is the path they are on.
    if (phrase === undefined && (await adoptFromPeer())) return;

    // The account went with the tab, and the unlock needs an id token to fetch
    // the share this browser's key is sealed against — so it is resumed first.
    let auth = getAuth();
    if (!auth) {
      const stored = persistedRefreshToken();
      if (!stored) {
        return setStep({ name: 'gone', why: 'This browser did not keep a way back in.' });
      }
      try {
        auth = await resumeFromRefreshToken(stored);
        setAuth(auth);
        setAccount(auth.email);
      } catch {
        // A refused token means the account was cut off while this browser was
        // away. The stored copy is rubbish and goes.
        clearPersistedAccount();
        return setStep({ name: 'gone', why: 'The account this browser held is no longer valid.' });
      }
    }

    let outcome;
    try {
      outcome = await unlockBrowser(auth.idToken, phrase);
    } catch {
      return setStep({ name: 'locked' });
    }

    switch (outcome.kind) {
      case 'opened':
        return setSession(outcome.session);
      case 'needs-passphrase':
        return setStep({ name: 'phrase', wrong: false });
      case 'wrong-passphrase':
        return setStep({ name: 'phrase', wrong: true });
      case 'deferred':
        return setStep({ name: 'locked' });
      case 'refused':
        return setStep({
          name: 'gone',
          why: 'Your phone cut this browser off, so the key it had stored is gone.',
        });
      default:
        return setStep({
          name: 'gone',
          why: 'What this browser had stored could not be opened, so it has been dropped.',
        });
    }
  }, []);

  useEffect(() => {
    void open();
  }, [open]);

  if (step.name === 'gone') {
    return (
      <section className="placeholder">
        <h1>This browser needs your phone again</h1>
        <p className="t-bodyProse">{step.why}</p>
        <p>
          <Link to="/connect">Connect this browser</Link>
        </p>
      </section>
    );
  }

  if (step.name === 'phrase') {
    return (
      <section className="placeholder">
        <h1>Unlock</h1>
        <PhraseForm
          label="Your unlock phrase"
          submit="Unlock"
          account={account}
          error={step.wrong ? 'That phrase did not open it. Nothing was lost — try again.' : null}
          onSubmit={(phrase) => void open(phrase)}
          aside={
            <p className="t-metaMono arrive-or">
              or <Link to="/connect">use your phone instead</Link>
            </p>
          }
        />
      </section>
    );
  }

  if (step.name === 'locked') {
    return (
      <section className="placeholder">
        <h1>Unlock this browser</h1>
        <p className="t-bodyProse">
          The key is still here, sealed{guard ? ` behind ${guardLabel(guard)}` : ''}. Nothing was
          lost — it just was not answered for.
        </p>
        <div className="stack">
          <button type="button" className="btn-primary" onClick={() => void open()}>
            Unlock
          </button>
        </div>
        <p className="t-metaMono arrive-or">
          or <Link to="/connect">use your phone instead</Link>
        </p>
      </section>
    );
  }

  return (
    <Loading
      title="Opening the vault…"
      detail={guard === 'hardware' ? 'waiting for this device’s unlock' : undefined}
    />
  );
}
